import React, { useState, useMemo, useCallback } from "react";
import {
  Box, Typography, Paper, Button, IconButton, Tooltip, Chip,
  Snackbar, Alert, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, TextField, Fab, InputAdornment,
  CircularProgress, Skeleton, Stack, Fade, Grow, Zoom, alpha, Grid
} from "@mui/material";
import {
  Edit as EditIcon,
  Add as AddIcon,
  Block as BlockIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Store as StoreIcon,
  Apartment as ApartmentIcon,
  CreditCard as CreditCardIcon,
  Category as CategoryIcon,
  ToggleOn as ToggleOnIcon,
  MoreVert as MoreVertIcon,
  TrendingUp as TrendingUpIcon
} from "@mui/icons-material";
import OrganizationProviderForm from "./OrganizationsProviderForm";
import { useOrganizations } from "../../../hooks/useOrganizations";
import { useAuthContext } from "../../../context/AuthProvider";

// Constantes
const ESTADOS = {
  ACTIVO: "A",
  INACTIVO: "I"
};

const TIPOS_ORGANIZACION = {
  PROVEEDOR: "PROVEEDOR",
  PROPIA: "PROPIA",
  CLIENTE: "CLIENTE"
};

// Helper para sanitizar texto
const sanitizeString = (value) => value?.toString().toLowerCase() || "";

// Componente de estadísticas
const StatsCard = ({ title, value, icon: Icon, color }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
      borderRadius: 3,
      border: `1px solid ${color}30`,
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: `0 8px 20px ${color}20`,
        borderColor: `${color}60`
      }
    }}
  >
    <Box display="flex" alignItems="center" justifyContent="space-between">
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: '0.5px' }}>
          {title}
        </Typography>
        <Typography variant="h4" fontWeight="bold" sx={{ mt: 1, color: color }}>
          {value}
        </Typography>
      </Box>
      <Box
        sx={{
          p: 1.5,
          borderRadius: 2,
          background: `${color}20`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Icon sx={{ color: color, fontSize: 28 }} />
      </Box>
    </Box>
  </Paper>
);

const OrganizationsProviderDashboard = () => {
  const [open, setOpen] = useState(false);
  const [editOrg, setEditOrg] = useState(null);
  const [alert, setAlert] = useState({ open: false, message: "", severity: "success" });
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { user } = useAuthContext();
  const { organizations = [], isLoading, error, createMutation, updateMutation, deleteMutation } = useOrganizations();

  // Mostrar alerta de error si hay error en la carga
  React.useEffect(() => {
    if (error) {
      showAlert(error.message || "Error al cargar organizaciones", "error");
    }
  }, [error]);

  const showAlert = useCallback((message, severity = "success") => {
    setAlert({ open: true, message, severity });
  }, []);

  const handleSubmit = useCallback((data) => {
    if (!user) {
      showAlert("Debe iniciar sesión", "error");
      return;
    }

    const mutation = editOrg ? updateMutation : createMutation;
    const payload = editOrg
      ? { id: editOrg.orgid, data: { ...data, updbyid: user.EmpID } }
      : { ...data, crebyid: user.EmpID };

    mutation.mutate(payload, {
      onSuccess: () => {
        showAlert(`Organización ${editOrg ? "actualizada" : "creada"} exitosamente`);
        handleCloseForm();
      },
      onError: (err) => showAlert(err.message || `Error al ${editOrg ? "actualizar" : "crear"}`, "error"),
    });
  }, [user, editOrg, createMutation, updateMutation, showAlert]);

  const handleDeactivate = useCallback((id, orgName) => {
    if (window.confirm(`¿Estás seguro de desactivar la organización "${orgName}"?`)) {
      deleteMutation.mutate(id, {
        onSuccess: () => showAlert("Organización desactivada exitosamente"),
        onError: (err) => showAlert(err.message || "Error al desactivar", "error"),
      });
    }
  }, [deleteMutation, showAlert]);

  const handleCloseForm = useCallback(() => {
    setOpen(false);
    setEditOrg(null);
  }, []);

  const getValidOrgTip = (value) => {
    if (!value) return TIPOS_ORGANIZACION.PROVEEDOR;
    if (Object.values(TIPOS_ORGANIZACION).includes(value)) return value;
    return TIPOS_ORGANIZACION.PROVEEDOR;
  };

  const handleEdit = useCallback((org) => {
    const safeOrg = {
      ...org,
      orgtip: getValidOrgTip(org.orgtip)
    };
    setEditOrg(safeOrg);
    setOpen(true);
  }, []);

  const handleCreate = useCallback(() => {
    setEditOrg(null);
    setOpen(true);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchTerm("");
    setPage(0);
  }, []);

  // Estadísticas
  const stats = useMemo(() => {
    const total = organizations.length;
    const activas = organizations.filter(org => org.estreg === ESTADOS.ACTIVO).length;
    const proveedores = organizations.filter(org => org.orgtip === TIPOS_ORGANIZACION.PROVEEDOR).length;
    return { total, activas, proveedores };
  }, [organizations]);

  // Filtrar organizaciones con manejo seguro de null
  const filteredData = useMemo(() => {
    if (!organizations.length) return [];

    const searchLower = sanitizeString(searchTerm);
    if (!searchLower) return organizations;

    return organizations.filter(org => {
      const searchFields = [
        org.orgnom,
        org.orgnomsoc,
        org.orgruc,
        org.orgtip
      ];

      return searchFields.some(field => sanitizeString(field).includes(searchLower));
    });
  }, [organizations, searchTerm]);

  // Paginación
  const paginatedData = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return filteredData.slice(start, end);
  }, [filteredData, page, rowsPerPage]);

  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  // Renderizado condicional de carga
  if (isLoading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Skeleton variant="text" width="60%" height={40} />
          <Skeleton variant="rectangular" height={56} />
          <Skeleton variant="rectangular" height={400} />
        </Stack>
      </Paper>
    );
  }

  return (
    <Fade in timeout={500}>
      <Box>
        {/* Header Premium */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 3,
            background: 'linear-gradient(135deg, #0A1929 0%, #0D2B42 50%, #0A1929 100%)',
            borderRadius: 4,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #3B82F6, #10B981, #3B82F6)',
              animation: 'gradient 3s ease infinite'
            }
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Box>
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{
                  color: '#fff',
                  letterSpacing: '-0.5px',
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2
                }}
              >
                Panel de Organizaciones
                <Chip
                  label={`${filteredData.length} registros`}
                  size="small"
                  sx={{
                    bgcolor: alpha('#fff', 0.1),
                    color: '#fff',
                    fontWeight: 600,
                    backdropFilter: 'blur(10px)'
                  }}
                />
              </Typography>
              <Typography variant="body2" sx={{ color: alpha('#fff', 0.7) }}>
                Gestión completa de tus organizaciones comerciales
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreate}
              sx={{
                bgcolor: '#fff',
                color: '#0A1929',
                '&:hover': {
                  bgcolor: alpha('#fff', 0.9),
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Nueva Organización
            </Button>
          </Box>
        </Paper>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <StatsCard
              title="TOTAL ORGANIZACIONES"
              value={stats.total}
              icon={StoreIcon}
              color="#3B82F6"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <StatsCard
              title="ORGANIZACIONES ACTIVAS"
              value={stats.activas}
              icon={ToggleOnIcon}
              color="#10B981"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <StatsCard
              title="PROVEEDORES"
              value={stats.proveedores}
              icon={TrendingUpIcon}
              color="#F59E0B"
            />
          </Grid>
        </Grid>

        {/* Tabla Principal */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            backdropFilter: 'blur(10px)',
            background: alpha('#fff', 0.02)
          }}
        >
          {/* Barra de búsqueda */}
          <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
            <TextField
              label="Buscar organización"
              placeholder="Nombre, razón social, RUC o tipo..."
              fullWidth
              size="medium"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: 3,
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#3B82F6'
                    }
                  }
                }
              }}
            />
          </Box>

          <TableContainer>
            <Table>
              <TableHead sx={{
                background: 'linear-gradient(135deg, #0A1929 0%, #0F2B42 100%)',
                '& .MuiTableCell-root': {
                  color: '#FFFFFF',
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  letterSpacing: '0.8px',
                  borderBottom: 'none',
                  py: 2,
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: '10%',
                    right: '10%',
                    height: '2px',
                    background: 'linear-gradient(90deg, #3B82F6, #60A5FA, #3B82F6)',
                    borderRadius: '2px',
                    opacity: 0,
                    transition: 'opacity 0.3s ease'
                  }
                },
                '&:hover .MuiTableCell-root::after': {
                  opacity: 1
                }
              }}>
                <TableRow>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <StoreIcon fontSize="small" sx={{ opacity: 0.9 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Nombre Comercial
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <ApartmentIcon fontSize="small" sx={{ opacity: 0.9 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Razón Social
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                      <CreditCardIcon fontSize="small" sx={{ opacity: 0.9 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        RUC
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                      <CategoryIcon fontSize="small" sx={{ opacity: 0.9 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Tipo
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                      <ToggleOnIcon fontSize="small" sx={{ opacity: 0.9 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Estado
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                      <MoreVertIcon fontSize="small" sx={{ opacity: 0.9 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Acciones
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <StoreIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2, opacity: 0.5 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          {searchTerm ? "No se encontraron resultados" : "No hay organizaciones registradas"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {searchTerm
                            ? "Intenta con otros términos de búsqueda"
                            : "Comienza creando tu primera organización"
                          }
                        </Typography>
                        {!searchTerm && (
                          <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={handleCreate}
                            sx={{ mt: 2 }}
                          >
                            Crear Organización
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map((org, index) => (
                    <Grow in timeout={300 * (index % 5)} key={org.orgid}>
                      <TableRow
                        hover
                        sx={{
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            bgcolor: alpha('#3B82F6', 0.04),
                            transform: 'scale(1.01)',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                          }
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {org.orgnom || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {org.orgnomsoc || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={org.orgruc || "—"}
                            size="small"
                            variant="outlined"
                            sx={{ fontFamily: 'monospace', fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={org.orgtip || "—"}
                            size="small"
                            sx={{
                              bgcolor: org.orgtip === TIPOS_ORGANIZACION.PROPIA ? alpha('#3B82F6', 0.1) :
                                org.orgtip === TIPOS_ORGANIZACION.PROVEEDOR ? alpha('#10B981', 0.1) :
                                  alpha('#F59E0B', 0.1),
                              color: org.orgtip === TIPOS_ORGANIZACION.PROPIA ? '#3B82F6' :
                                org.orgtip === TIPOS_ORGANIZACION.PROVEEDOR ? '#10B981' : '#F59E0B',
                              fontWeight: 600
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={org.estreg === ESTADOS.ACTIVO ? "ACTIVO" : "INACTIVO"}
                            color={org.estreg === ESTADOS.ACTIVO ? "success" : "default"}
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={1} justifyContent="center">
                            <Tooltip title="Editar organización" arrow>
                              <IconButton
                                onClick={() => handleEdit(org)}
                                size="small"
                                sx={{
                                  color: '#3B82F6',
                                  '&:hover': {
                                    bgcolor: alpha('#3B82F6', 0.1),
                                    transform: 'scale(1.1)'
                                  },
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {org.estreg === ESTADOS.ACTIVO && (
                              <Tooltip title="Desactivar organización" arrow>
                                <IconButton
                                  onClick={() => handleDeactivate(org.orgid, org.orgnom)}
                                  size="small"
                                  sx={{
                                    color: '#EF4444',
                                    '&:hover': {
                                      bgcolor: alpha('#EF4444', 0.1),
                                      transform: 'scale(1.1)'
                                    },
                                    transition: 'all 0.2s ease'
                                  }}
                                >
                                  <BlockIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    </Grow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Filas por página"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            sx={{
              borderTop: '1px solid',
              borderColor: 'divider',
              '& .MuiTablePagination-toolbar': {
                py: 2
              }
            }}
          />
        </Paper>

        {/* Formulario */}
        <OrganizationProviderForm
          open={open}
          handleClose={handleCloseForm}
          onSubmit={handleSubmit}
          defaultValues={editOrg || {}}
        />

        {/* Snackbar de notificaciones */}
        <Snackbar
          open={alert.open}
          autoHideDuration={4000}
          onClose={() => setAlert(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          TransitionComponent={Zoom}
        >
          <Alert
            severity={alert.severity}
            variant="filled"
            onClose={() => setAlert(prev => ({ ...prev, open: false }))}
            sx={{
              borderRadius: 2,
              boxShadow: '0 8px 20px rgba(0,0,0,0.15)'
            }}
          >
            {alert.message}
          </Alert>
        </Snackbar>
      </Box>
    </Fade>
  );
};

export default OrganizationsProviderDashboard;